import React, { useRef, useState, useEffect } from 'react';
import {
  Box, Grid, Card, Stack, Typography, Chip, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Divider, Tooltip, alpha, useTheme, Snackbar, Alert,
  Skeleton, InputAdornment, CircularProgress,
} from '@mui/material';
import {
  Plus, FileText, Trash2, Copy, MessageSquare, X, Image as ImageIcon, Video, Upload, File,
  Bold, Italic, Strikethrough, Phone, ExternalLink, CornerDownLeft, Send, RefreshCw, Braces, Pencil,
} from 'lucide-react';
import {
  TEMPLATE_STATUS, TEMPLATE_CATEGORIES, TEMPLATE_LANGUAGES, TEMPLATE_BUTTON_TYPES,
  WA_TEST_NUMBER, normalizeButtons, fmtDateTime, WA_GREEN, WA_TEAL,
} from './waData';
import WaSectionHeader, { waGridItem, useWaMobile, waDialogProps, waDialogActionsSx } from './WaLayout';
import { waApi } from './waApi';
import { onWaEvent } from './waSocket';
import { STATIC_IMAGE_URL, API_URL } from '../../utils/axios';
import { notify } from '../../utils/notificationService';

const BACKEND_URL = (API_URL || '').replace(/\/api\/?$/, '');

// A stored template media path is server-relative (e.g. /wa_template_media/x.jpg);
// make it absolute for <img> src. Blob/data/http URLs (a freshly picked file) pass through.
const mediaSrc = (url) => {
  if (!url) return '';
  if (/^(blob:|data:|https?:)/.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  if (path.startsWith('/wa_template_media')) {
    return `${BACKEND_URL}${path}`;
  }
  return `${STATIC_IMAGE_URL}${path}`;
};

const StatusChip = ({ status }) => {
  const meta = TEMPLATE_STATUS[status] || TEMPLATE_STATUS.PAUSED;
  return <Chip label={meta.label} size="small" color={meta.color === 'default' ? undefined : meta.color}
    variant={meta.color === 'default' ? 'outlined' : 'filled'} sx={{ fontWeight: 700 }} />;
};

// Meta only returns a coarse reason code (no free-text explanation via the API —
// the detailed note lives in WhatsApp Manager). Translate the codes into plain
// English plus a concrete fix so a rejection is actionable.
const REJECTION_REASONS = {
  INCORRECT_CATEGORY: {
    text: 'The content doesn’t match the chosen category.',
    fix: 'Promotional/offer wording must be MARKETING; only transactional notices qualify as UTILITY. Edit the category to MARKETING and resubmit.',
  },
  TAG_CONTENT_MISMATCH: {
    text: 'The content doesn’t match the chosen category.',
    fix: 'Switch the category (usually to MARKETING) so it matches the wording, then resubmit.',
  },
  PROMOTIONAL: {
    text: 'A UTILITY template was found to contain marketing/promotional content.',
    fix: 'Change the category to MARKETING, or remove the promotional wording, then resubmit.',
  },
  INVALID_FORMAT: {
    text: 'The template formatting is invalid.',
    fix: 'Check for missing variable examples, empty/extra {{placeholders}}, unbalanced formatting, or links/emojis in the header. Fix and resubmit.',
  },
  ABUSIVE_CONTENT: {
    text: 'Meta flagged the wording as abusive or against policy.',
    fix: 'Rewrite to remove anything that could read as spammy, threatening, or misleading, then resubmit.',
  },
  SCAM: {
    text: 'Meta flagged the template as a potential scam.',
    fix: 'Remove suspicious links, money/credential requests, or urgency wording; use your verified business name. Then resubmit.',
  },
};

const friendlyReason = (code) =>
  REJECTION_REASONS[code] || { text: code ? code.replace(/_/g, ' ').toLowerCase() : '', fix: '' };

// Inline explanation shown on rejected/paused template cards and the test dialog.
const RejectionNote = ({ reason, sx }) => {
  if (!reason) return null;
  const { text, fix } = friendlyReason(reason);
  return (
    <Alert severity="error" sx={{ fontSize: 12, py: 0.5, '& .MuiAlert-message': { py: 0.5 }, ...sx }}>
      <b>Why it was rejected:</b> {text}
      {fix ? <><br /><b>How to fix:</b> {fix}</> : null}
      <br /><Typography component="span" sx={{ fontSize: 10.5, color: 'text.disabled' }}>
        Meta code: {reason} · full notes in WhatsApp Manager → Message Templates
      </Typography>
    </Alert>
  );
};

// Parse WhatsApp-style formatting into React nodes so the preview matches how the
// message actually renders on a phone:  *bold*  _italic_  ~strike~  ```mono```
const RICH_RE = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
const renderRichText = (text) =>
  String(text).split(RICH_RE).filter((p) => p !== undefined && p !== '').map((p, i) => {
    if (p.startsWith('```') && p.endsWith('```')) {
      return <Box key={i} component="span" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: 0.5 }}>{p.slice(3, -3)}</Box>;
    }
    if (p.startsWith('*') && p.endsWith('*')) return <strong key={i}>{p.slice(1, -1)}</strong>;
    if (p.startsWith('_') && p.endsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('~') && p.endsWith('~')) return <span key={i} style={{ textDecoration: 'line-through' }}>{p.slice(1, -1)}</span>;
    return p;
  });

// Dynamic placeholders, e.g. "Hey {{user}}" or "{{1}}". Pull them out in order,
// substitute their sample values for the preview, and map a saved variable list
// back to a { name: example } lookup.
const VAR_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
const extractVars = (text = '') => {
  const out = []; const re = new RegExp(VAR_RE); let m;
  while ((m = re.exec(text))) if (!out.includes(m[1])) out.push(m[1]);
  return out;
};
const substituteVars = (text = '', samples = {}) =>
  String(text).replace(VAR_RE, (m, n) => (samples[n] && String(samples[n]).trim() ? samples[n] : m));
const varsToSamples = (variables = []) =>
  variables.reduce((acc, v) => { if (v && v.name) acc[v.name] = v.example || ''; return acc; }, {});

// Icon shown on a rendered action button, by button type.
const ButtonIcon = ({ type, size = 14 }) => {
  if (type === 'PHONE_NUMBER') return <Phone size={size} />;
  if (type === 'URL') return <ExternalLink size={size} />;
  return <CornerDownLeft size={size} />;
};

// WhatsApp message-bubble preview. Used full-size in the builder dialog and in
// a `compact` variant inside each template card so the list shows exactly how
// the message will render. In compact mode the long body is clamped so cards
// stay a tidy, uniform height.
const TemplatePreview = ({ form, compact = false }) => {
  const theme = useTheme();
  const bodyFontSize = compact ? 12 : 13.5;
  const samples = form.samples || {};
  const buttons = (form.buttons || []).filter((b) => b.text && b.text.trim());
  // WhatsApp renders call-to-action buttons (links/phone) attached to the bottom
  // of the message bubble, but quick-reply buttons as separate pills below it.
  const ctaButtons = buttons.filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
  const quickButtons = buttons.filter((b) => b.type === 'QUICK_REPLY');
  return (
    <Box sx={{ 
      p: compact ? 1.5 : 2.5, 
      borderRadius: 3,
      bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc',
      backgroundImage: theme.palette.mode === 'dark' 
        ? 'radial-gradient(rgba(255, 255, 255, 0.015) 1px, transparent 0)' 
        : 'radial-gradient(rgba(15, 23, 42, 0.025) 1px, transparent 0)', 
      backgroundSize: '16px 16px',
      display: 'flex', 
      flexDirection: 'column',
      border: `1px solid ${theme.palette.divider}`,
      ...(compact ? { flex: 1, width: '100%' } : { minHeight: 220 }) 
    }}>
      {!compact && (
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.disabled', letterSpacing: 0.5, mb: 1.5, textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>
          PREVIEW
        </Typography>
      )}
      <Box sx={{ 
        alignSelf: 'flex-start', 
        maxWidth: compact ? '98%' : '90%', 
        bgcolor: theme.palette.mode === 'dark' ? '#202c33' : '#ffffff',
        borderRadius: '12px', 
        borderTopLeftRadius: '4px',
        p: 1.5, 
        border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.65)}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 8px rgba(0, 0, 0, 0.2)'
          : '0 2px 10px rgba(145, 158, 171, 0.05)',
      }}>
        {(form.headerType === 'IMAGE' || form.headerType === 'VIDEO' || form.headerType === 'DOCUMENT') && (
          form.headerMediaUrl ? (
            form.headerType === 'IMAGE' ? (
              <Box
                component="img"
                src={form.headerMediaUrl}
                alt="header"
                sx={{
                  width: '100%',
                  maxHeight: 120,
                  objectFit: 'cover',
                  borderRadius: '8px',
                  mb: 1,
                  display: 'block',
                  bgcolor: (theme) => alpha(theme.palette.divider, 0.1),
                }}
                onError={(e) => {
                  const currentSrc = e.currentTarget.src || '';
                  if (!e.currentTarget.dataset.retried) {
                    e.currentTarget.dataset.retried = 'true';
                    if (currentSrc.includes('amazonaws.com') && BACKEND_URL) {
                      const filename = currentSrc.split('/').pop();
                      e.currentTarget.src = `${BACKEND_URL}/wa_template_media/${filename}`;
                    } else if (STATIC_IMAGE_URL && !currentSrc.includes('amazonaws.com')) {
                      const filename = currentSrc.split('/').pop();
                      e.currentTarget.src = `${STATIC_IMAGE_URL}/wa_template_media/${filename}`;
                    }
                  }
                }}
              />
            ) : form.headerType === 'VIDEO' ? (
              <Box component="video" src={form.headerMediaUrl} controls
                sx={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: '8px', mb: 1, display: 'block', bgcolor: '#000' }} />
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                mb: 1
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: alpha(WA_TEAL, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <File size={16} color={WA_TEAL} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', fontFamily: 'Inter, sans-serif' }} noWrap>
                    {form.headerMediaName || 'Document.pdf'}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                    Document Template
                  </Typography>
                </Box>
              </Box>
            )
          ) : (
            <Box sx={{ 
              height: form.headerType === 'DOCUMENT' ? 44 : (compact ? 64 : 90), 
              borderRadius: '8px', 
              mb: 1, 
              display: 'flex',
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 1,
              background: `linear-gradient(135deg, ${alpha(WA_TEAL, 0.15)}, ${alpha(WA_GREEN, 0.05)})` 
            }}>
              {form.headerType === 'VIDEO' ? <Video size={18} color={WA_TEAL} />
                : form.headerType === 'DOCUMENT' ? <File size={16} color={WA_TEAL} />
                : <ImageIcon size={18} color={WA_TEAL} />}
              <Typography sx={{ fontSize: compact ? 11 : 12, fontWeight: 700, color: 'text.secondary', fontFamily: 'Inter, sans-serif' }} noWrap>
                {form.headerType === 'VIDEO' ? '[Video]' : form.headerType === 'DOCUMENT' ? '[Document]' : '[Image]'}
              </Typography>
            </Box>
          )
        )}
        {form.headerType === 'TEXT' && form.header && (
          <Typography sx={{ fontSize: bodyFontSize, fontWeight: 800, mb: 0.5, fontFamily: 'Outfit, sans-serif' }}>
            {renderRichText(substituteVars(form.header, samples))}
          </Typography>
        )}
        <Typography sx={{ 
          fontSize: bodyFontSize, 
          lineHeight: 1.45, 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word',
          fontFamily: 'Inter, sans-serif',
          color: 'text.primary',
          ...(compact && { display: '-webkit-box', WebkitLineClamp: 7, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) 
        }}>
          {form.body ? renderRichText(substituteVars(form.body, samples)) : 'Your message body will appear here…'}
        </Typography>
        {form.footer && (
          <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 1, fontFamily: 'Inter, sans-serif' }} noWrap={compact}>
            {form.footer}
          </Typography>
        )}
        {/* Call-to-action buttons sit INSIDE the bubble, attached at the bottom
            behind a full-width divider — exactly how WhatsApp renders them. */}
        {ctaButtons.length > 0 && (
          <Box sx={{ mt: 1.5, mx: -1.5 }}>
            {ctaButtons.map((b, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="center" justifyContent="center"
                sx={{ 
                  borderTop: `1px solid ${theme.palette.divider}`, 
                  py: 1, 
                  px: 1.5,
                  color: '#00a5f4', 
                  fontSize: compact ? 11.5 : 12.5, 
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.4),
                  }
                }}>
                <ButtonIcon type={b.type} size={compact ? 12 : 13} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.text}</Box>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
      {/* Quick-reply buttons render as separate pills below the bubble. */}
      {quickButtons.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1, alignSelf: 'flex-start', width: compact ? '98%' : '90%' }}>
          {quickButtons.map((b, i) => (
            <Stack key={i} direction="row" spacing={0.5} alignItems="center" justifyContent="center"
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? '#202c33' : '#ffffff', 
                borderRadius: '8px', 
                py: 1, 
                px: 1.5,
                fontSize: compact ? 11.5 : 12.5, 
                fontWeight: 700, 
                color: '#00a5f4', 
                boxShadow: theme.shadows[1],
                border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.65)}`,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2],
                }
              }}>
              <ButtonIcon type={b.type} size={compact ? 12 : 13} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif' }}>{b.text}</Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

// Map a stored template (from the API) to the shape TemplatePreview expects.
const toPreviewForm = (t) => ({
  headerType: t.headerType || 'NONE',
  header: t.header || '',
  headerMediaName: t.headerMediaName || '',
  headerMediaUrl: mediaSrc(t.headerMediaUrl),
  body: t.body || '',
  footer: t.footer || '',
  buttons: normalizeButtons(t.buttons || []),
  samples: varsToSamples(t.variables || []),
});

const BLANK_TEMPLATE = { name: '', category: 'MARKETING', language: 'en_US', headerType: 'NONE', header: '', headerMediaName: '', headerMediaUrl: '', headerMediaFile: null, body: '', footer: '', buttons: [], samples: {} };

// Build the request body for create/update. When a media-header file is attached
// we POST multipart/form-data (file + JSON-encoded arrays); otherwise a plain
// JSON object. The backend accepts both.
const toRequest = (payload) => {
  const { headerMediaFile, ...rest } = payload;
  if (!headerMediaFile) return rest;
  const fd = new FormData();
  fd.append('template_media', headerMediaFile);
  Object.entries(rest).forEach(([k, v]) => {
    fd.append(k, Array.isArray(v) || (v && typeof v === 'object') ? JSON.stringify(v) : v ?? '');
  });
  return fd;
};

const BLANK_BUTTON = { QUICK_REPLY: { type: 'QUICK_REPLY', text: '', value: '' },
  PHONE_NUMBER: { type: 'PHONE_NUMBER', text: '', value: '' },
  URL: { type: 'URL', text: '', value: '' } };

const FMT_BUTTONS = [
  { icon: Bold, marker: '*', title: 'Bold' },
  { icon: Italic, marker: '_', title: 'Italic' },
  { icon: Strikethrough, marker: '~', title: 'Strikethrough' },
];

// Auto-draft for the "Create" flow so an accidental close / refresh doesn't wipe
// a half-built template. We persist everything except the picked file (a File
// can't be serialised) and the blob preview URL — the media must be re-attached.
const DRAFT_KEY = 'wa_template_draft';
const loadDraft = () => { try { const r = localStorage.getItem(DRAFT_KEY); return r ? JSON.parse(r) : null; } catch (e) { return null; } };
const saveDraft = (form) => {
  try { const { headerMediaFile, headerMediaUrl, ...rest } = form; localStorage.setItem(DRAFT_KEY, JSON.stringify(rest)); } catch (e) { /* quota/serialisation — ignore */ }
};
const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ } };

const TemplateDialog = ({ open, onClose, onSubmit, initial, isEdit = false }) => {
  const theme = useTheme();
  const mobile = useWaMobile();
  const [form, setForm] = useState(BLANK_TEMPLATE);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [draftRestored, setDraftRestored] = useState(false);
  const mediaInputRef = useRef(null);
  const bodyRef = useRef(null);
  // The object URL we created for the picked-media preview, so we can revoke it
  // when it's replaced or the dialog unmounts (createObjectURL leaks otherwise).
  const blobUrlRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const handleHeaderTypeChange = (e) => {
    const nextType = e.target.value;
    setForm((f) => {
      const isOriginalType = nextType === initial?.headerType;
      return {
        ...f,
        headerType: nextType,
        headerMediaName: isOriginalType ? (initial?.headerMediaName || '') : '',
        headerMediaUrl: isOriginalType ? (initial?.headerMediaUrl || '') : '',
        headerMediaFile: null,
      };
    });
  };

  // When opened, seed the form. `initial` may come from a Duplicate or Edit
  // action and can carry buttons (string|object) and saved variables.
  // "Pure create" (no edit, no duplicate seed) is the only flow that uses drafts.
  const isPureCreate = !isEdit && !initial;

  useEffect(() => {
    if (!open) return;
    setSubmitError('');
    setSubmitting(false);
    if (isPureCreate) {
      const draft = loadDraft();
      if (draft && (draft.name?.trim() || draft.body?.trim())) {
        setForm({ ...BLANK_TEMPLATE, ...draft, headerMediaFile: null, headerMediaUrl: '', buttons: normalizeButtons(draft.buttons || []), samples: draft.samples || {} });
        setDraftRestored(true);
        return;
      }
    }
    setDraftRestored(false);
    setForm({
      ...BLANK_TEMPLATE, ...(initial || {}),
      buttons: normalizeButtons(initial?.buttons || []),
      samples: initial?.samples || varsToSamples(initial?.variables || []),
    });
  }, [open, initial, isEdit, isPureCreate]);

  // Auto-save the draft as the operator types (create-from-scratch only).
  useEffect(() => {
    if (open && isPureCreate && (form.name?.trim() || form.body?.trim())) saveDraft(form);
  }, [form, open, isPureCreate]);

  const discardDraft = () => { clearDraft(); setDraftRestored(false); setForm(BLANK_TEMPLATE); };

  const onPickMedia = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Revoke the previous preview URL before minting a new one so picking
      // several files in a row doesn't leak the earlier blobs.
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;
      setForm((f) => ({ ...f, headerMediaName: file.name, headerMediaFile: file, headerMediaUrl: url }));
    }
    e.target.value = '';
  };

  // Revoke any outstanding preview blob URL when the dialog closes or unmounts.
  // (The dialog stays mounted while hidden, so closing won't trigger an unmount —
  // we revoke on the open→closed transition too.)
  useEffect(() => {
    if (!open && blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    return () => { if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; } };
  }, [open]);

  // Wrap the current Body selection in a WhatsApp formatting marker (*, _, ~, ```).
  const wrapBody = (marker) => {
    const el = bodyRef.current;
    const body = form.body;
    const s = el?.selectionStart ?? body.length;
    const e = el?.selectionEnd ?? body.length;
    const sel = body.slice(s, e) || 'text';
    const next = body.slice(0, s) + marker + sel + marker + body.slice(e);
    setForm((f) => ({ ...f, body: next }));
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(s + marker.length, s + marker.length + sel.length); } });
  };

  // Insert a placeholder at the cursor and pre-select the name so it can be typed
  // over immediately — turning "Hey " into "Hey {{variable}}".
  const insertVariable = () => {
    const el = bodyRef.current;
    const body = form.body;
    const pos = el?.selectionStart ?? body.length;
    const token = '{{variable}}';
    setForm((f) => ({ ...f, body: body.slice(0, pos) + token + body.slice(pos) }));
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(pos + 2, pos + 10); } });
  };

  const setSample = (name, value) => setForm((f) => ({ ...f, samples: { ...(f.samples || {}), [name]: value } }));

  // Variables currently present in the header text + body, in order.
  const headerVars = form.headerType === 'TEXT' ? extractVars(form.header) : [];
  const detectedVars = [...new Set([...headerVars, ...extractVars(form.body)])];

  // ---- button builder helpers ----
  const buttons = form.buttons || [];
  const addButton = (type) => setForm((f) => ({ ...f, buttons: [...(f.buttons || []), { ...BLANK_BUTTON[type] }] }));
  const updateButton = (idx, patch) => setForm((f) => ({
    ...f, buttons: (f.buttons || []).map((b, i) => (i === idx ? { ...b, ...patch } : b)),
  }));
  const removeButton = (idx) => setForm((f) => ({ ...f, buttons: (f.buttons || []).filter((_, i) => i !== idx) }));

  const phoneCount = buttons.filter((b) => b.type === 'PHONE_NUMBER').length;
  const urlCount = buttons.filter((b) => b.type === 'URL').length;

  const submit = async () => {
    // Drop empty/incomplete buttons; the backend validates again before Meta.
    const cleanButtons = buttons
      .map((b) => ({ type: b.type, text: (b.text || '').trim(), value: (b.value || '').trim() }))
      .filter((b) => b.text && (b.type === 'QUICK_REPLY' || b.value))
      .slice(0, 10);
    const variables = detectedVars.map((v) => ({ name: v, example: (form.samples?.[v] || '').trim() }));
    const isMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType);
    setSubmitError('');
    setSubmitting(true);
    // Wait for the real result; only close on success so a Meta rejection (e.g. a
    // UTILITY template flagged as marketing) is shown inline instead of vanishing.
    const result = await onSubmit({
      name: form.name, category: form.category, language: form.language,
      headerType: form.headerType, header: form.headerType === 'TEXT' ? form.header : '',
      headerMediaName: isMedia ? form.headerMediaName : '',
      headerMediaFile: isMedia ? form.headerMediaFile : null,
      body: form.body, footer: form.footer,
      buttons: cleanButtons, variables,
    });
    setSubmitting(false);
    if (result?.ok) {
      clearDraft();
      setDraftRestored(false);
      setForm(BLANK_TEMPLATE);
      onClose();
    } else {
      setSubmitError(result?.error || 'Something went wrong. Please try again.');
    }
  };

  // Meta requires a sample value for every placeholder before it will review.
  const varsFilled = detectedVars.every((v) => (form.samples?.[v] || '').trim());
  // A media header needs a sample file before it can be submitted.
  // For edits, we can keep the existing sample if the media type hasn't changed.
  const isMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType);
  const mediaOk = !isMedia || 
                  Boolean(form.headerMediaFile) || 
                  (isEdit && form.headerType === initial?.headerType && Boolean(form.headerMediaUrl));
  const valid = form.name.trim() && form.body.trim() && varsFilled && mediaOk;

  return (
    <Dialog open={open} {...waDialogProps(mobile, 'md')}
      onClose={(e, reason) => { if (submitting || reason === 'backdropClick' || reason === 'escapeKeyDown') return; onClose(); }}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isEdit ? 'Edit Message Template' : 'Create Message Template'}
        <IconButton size="small" onClick={onClose} disabled={submitting}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {submitError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>{submitError}</Alert>}
        {isPureCreate && draftRestored && (
          <Alert severity="info" sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" onClick={discardDraft}>Start fresh</Button>}>
            Restored your unsaved draft.{form.headerMediaName ? ' Re-attach the media file before submitting.' : ''}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField label="Template name" size="small" fullWidth value={form.name} onChange={set('name')}
                disabled={isEdit}
                helperText={isEdit ? 'Name can’t be changed after creation.' : 'Lowercase, no spaces (e.g. order_followup). Spaces auto-convert.'} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField select label="Category" size="small" fullWidth value={form.category} onChange={set('category')}>
                  {TEMPLATE_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField select label="Language" size="small" fullWidth value={form.language} onChange={set('language')}
                  disabled={isEdit} helperText={isEdit ? 'Locked' : undefined}>
                  {TEMPLATE_LANGUAGES.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField select label="Header" size="small" fullWidth value={form.headerType} onChange={handleHeaderTypeChange}>
                <MenuItem value="NONE">None</MenuItem>
                <MenuItem value="TEXT">Text</MenuItem>
                <MenuItem value="IMAGE">Image</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="DOCUMENT">Document</MenuItem>
              </TextField>
              {form.headerType === 'TEXT' && (
                <TextField label="Header text" size="small" fullWidth value={form.header} onChange={set('header')} />
              )}
              {(form.headerType === 'IMAGE' || form.headerType === 'VIDEO' || form.headerType === 'DOCUMENT') && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap">
                  <Button
                    variant={form.headerMediaName || form.headerMediaUrl ? "contained" : "outlined"}
                    color={form.headerMediaName || form.headerMediaUrl ? "primary" : "inherit"}
                    size="small"
                    startIcon={form.headerMediaName || form.headerMediaUrl ? <RefreshCw size={15} /> : <Upload size={15} />}
                    onClick={() => mediaInputRef.current?.click()}
                  >
                    {form.headerMediaName || form.headerMediaUrl
                      ? `Update ${form.headerType.toLowerCase()}`
                      : `Upload ${form.headerType.toLowerCase()}`}
                  </Button>
                  {(form.headerMediaName || form.headerMediaUrl) && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Trash2 size={14} />}
                      onClick={() => {
                        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
                        setForm((f) => ({ ...f, headerMediaName: '', headerMediaFile: null, headerMediaUrl: '' }));
                      }}
                    >
                      Remove
                    </Button>
                  )}
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', minWidth: 0, maxWidth: 220 }} noWrap title={form.headerMediaName || ''}>
                    {form.headerMediaName ? (
                      <span>
                        {form.headerMediaFile ? '🟢 New: ' : '📁 Current: '}
                        <strong>{form.headerMediaName}</strong>
                      </span>
                    ) : (
                      'No file selected'
                    )}
                  </Typography>
                  <input
                    ref={mediaInputRef}
                    type="file"
                    hidden
                    onChange={onPickMedia}
                    accept={
                      form.headerType === 'IMAGE'
                        ? 'image/jpeg,image/png'
                        : form.headerType === 'VIDEO'
                        ? 'video/mp4'
                        : 'application/pdf'
                    }
                  />
                </Stack>
              )}

              {/* Body with a WhatsApp formatting toolbar */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 0.25, sm: 0.5 }, mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>Body</Typography>
                  {FMT_BUTTONS.map((b) => {
                    const Icon = b.icon;
                    return (
                      <Tooltip key={b.marker} title={b.title}>
                        <IconButton size="small" onClick={() => wrapBody(b.marker)} sx={{ color: 'text.secondary' }}><Icon size={15} /></IconButton>
                      </Tooltip>
                    );
                  })}
                  <Tooltip title="Monospace"><IconButton size="small" onClick={() => wrapBody('```')} sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 800 }}>{'</>'}</IconButton></Tooltip>
                  <Tooltip title="Insert variable"><IconButton size="small" onClick={insertVariable} sx={{ color: 'text.secondary' }}><Braces size={15} /></IconButton></Tooltip>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>*bold* _italic_ ~strike~</Typography>
                </Box>
                <TextField size="small" fullWidth multiline minRows={3} value={form.body} onChange={set('body')} inputRef={bodyRef}
                  placeholder="e.g. Hey {{user}}, your order {{order_id}} has shipped!"
                  helperText="Insert a variable like {{user}} for values filled in at send time." />
              </Box>

              {/* Dynamic variables — sample values Meta needs to review the template */}
              {detectedVars.length > 0 && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                    <Braces size={14} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>Variable sample values</Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {detectedVars.map((v) => (
                      <Stack key={v} direction="row" spacing={1} alignItems="center">
                        <Chip label={`{{${v}}}`} size="small" sx={{ fontWeight: 700, fontFamily: 'monospace' }} />
                        <TextField size="small" fullWidth label="Sample value"
                          value={form.samples?.[v] ?? (v === '1' || v === 'user' ? 'Rahul' : v === '2' ? 'Mumbai' : '')}
                          onChange={(e) => setSample(v, e.target.value)}
                          placeholder={v === '1' || v === 'user' ? 'e.g. Rahul' : `Example for {{${v}}}`} />
                      </Stack>
                    ))}
                  </Stack>
                  <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.75 }}>
                    Shown to Meta as examples and previewed on the right. Real values are supplied when the message is sent.
                  </Typography>
                </Box>
              )}

              <TextField label="Footer (optional)" size="small" fullWidth value={form.footer} onChange={set('footer')} />

              {/* Action buttons builder */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>Buttons (optional)</Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>{buttons.length}/10 · max 1 phone · max 2 links</Typography>
                </Stack>

                <Stack spacing={1}>
                  {buttons.map((b, idx) => (
                    <Box key={idx} sx={{ p: 1, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.neutral }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
                        <TextField select size="small" label="Type" value={b.type}
                          onChange={(e) => updateButton(idx, { type: e.target.value, value: '' })}
                          sx={{ width: { xs: '100%', md: 170 } }}>
                          {TEMPLATE_BUTTON_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              <Stack direction="row" spacing={1} alignItems="center"><ButtonIcon type={t.value} size={14} /><span>{t.label}</span></Stack>
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField size="small" label="Button text" value={b.text}
                          onChange={(e) => updateButton(idx, { text: e.target.value })}
                          sx={{ flex: 1, width: '100%' }} inputProps={{ maxLength: 25 }}
                          helperText={`${(b.text || '').length}/25`}
                          InputProps={{ startAdornment: (
                            <InputAdornment position="start"><ButtonIcon type={b.type} /></InputAdornment>
                          ) }} />
                        {b.type === 'PHONE_NUMBER' && (
                          <TextField size="small" label="Phone number" placeholder="+91…" value={b.value}
                            onChange={(e) => updateButton(idx, { value: e.target.value })} sx={{ flex: 1, width: '100%' }} />
                        )}
                        {b.type === 'URL' && (
                          <TextField size="small" label="Website URL" placeholder="https://…" value={b.value}
                            onChange={(e) => updateButton(idx, { value: e.target.value })} sx={{ flex: 1, width: '100%' }} />
                        )}
                        <Tooltip title="Remove button">
                          <IconButton size="small" onClick={() => removeButton(idx)} sx={{ color: 'error.main', mt: { md: 0.5 } }}><X size={16} /></IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1}
                  justifyContent={{ xs: 'center', sm: 'flex-start' }}
                  sx={{ mt: buttons.length ? 1.25 : 0, flexWrap: 'wrap', gap: 1,
                    '& > span': { width: { xs: '100%', sm: 'auto' } },
                    '& > span > button': { width: { xs: '100%', sm: 'auto' } } }}>
                  <Tooltip title={buttons.length >= 10 ? 'Up to 10 buttons' : 'Sends the button label back as a reply'}>
                    <span>
                      <Button size="small" variant="outlined" startIcon={<CornerDownLeft size={14} />}
                        disabled={buttons.length >= 10} onClick={() => addButton('QUICK_REPLY')}>Quick reply</Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={phoneCount >= 1 ? 'Only 1 phone button allowed' : buttons.length >= 10 ? 'Up to 10 buttons' : 'Tap-to-call button'}>
                    <span>
                      <Button size="small" variant="outlined" startIcon={<Phone size={14} />}
                        disabled={buttons.length >= 10 || phoneCount >= 1} onClick={() => addButton('PHONE_NUMBER')}>Phone</Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={urlCount >= 2 ? 'Up to 2 website buttons' : buttons.length >= 10 ? 'Up to 10 buttons' : 'Opens a website link'}>
                    <span>
                      <Button size="small" variant="outlined" startIcon={<ExternalLink size={14} />}
                        disabled={buttons.length >= 10 || urlCount >= 2} onClick={() => addButton('URL')}>Website</Button>
                    </span>
                  </Tooltip>
                </Stack>
                {buttons.length === 0 && (
                  <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 1 }}>
                    Add up to 10 buttons — <b>Quick reply</b> (sends text back), <b>Phone</b> (tap to call), or <b>Website</b> (opens a link).
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <TemplatePreview form={form} />
            <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1.25 }}>
              {isEdit
                ? 'Saving sends the edit back to Meta for review — the template returns to In Review until re-approved.'
                : 'After submitting, Meta reviews the template. Once it shows Approved, use “Send test” on its card to fire it at a number.'}
            </Typography>
            {isPureCreate && (
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.75 }}>
                💾 Your draft is saved automatically — closing or refreshing won’t lose it (the media file must be re-attached).
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={waDialogActionsSx}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>Cancel</Button>
        <Button variant="contained" disabled={!valid || submitting} onClick={submit}
          startIcon={submitting ? <CircularProgress size={15} color="inherit" /> : null}>
          {submitting ? (isEdit ? 'Saving…' : 'Submitting…') : (isEdit ? 'Save & Resubmit' : 'Submit for Approval')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Send-a-test dialog: fires one template message at a typed number so the
// operator can see it land on a real handset before using it in a broadcast.
const TestTemplateDialog = ({ template, onClose, onResult, onSent }) => {
  const mobile = useWaMobile();
  const [phone, setPhone] = useState(WA_TEST_NUMBER);
  const [vars, setVars] = useState({});
  const [sending, setSending] = useState(false);
  const open = Boolean(template);
  const tplVars = template?.variables || [];

  // Seed each {{variable}} with its saved example so the test renders something
  // sensible out of the box; the operator can overwrite with real values.
  useEffect(() => {
    if (open) {
      setPhone(WA_TEST_NUMBER);
      setVars((template?.variables || []).reduce((acc, v) => { acc[v.name] = v.example || ''; return acc; }, {}));
    }
  }, [open, template]);

  const setVar = (name, value) => setVars((p) => ({ ...p, [name]: value }));

  const send = async () => {
    setSending(true);
    try {
      const res = await waApi.testTemplate(template.id, phone, vars);
      // Track this test's message id so a later async failure (e.g. marketing
      // pacing) is shown ONLY for the test you sent — not for broadcast traffic.
      if (!res?.simulated && res?.messageId) onSent?.(res.messageId);
      onResult(res?.simulated
        ? 'Test send simulated (live sending is off) — flip WHATSAPP_LIVE_SEND to send for real.'
        : `Test sent to ${phone}`);
    } catch (e) {
      onResult(e?.response?.data?.error || e?.response?.data?.msg || 'Test send failed. Please try again.');
    } finally {
      setSending(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} {...waDialogProps(mobile, 'xs')}>
      <DialogTitle sx={{ fontWeight: 800 }}>Send a test</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
          Send <b>{template?.name}</b> to a phone number to check how it renders.
        </Typography>
        <TextField label="Phone number" size="small" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="9909719458"
          InputProps={{ startAdornment: <InputAdornment position="start"><Phone size={15} /></InputAdornment> }}
          helperText="10-digit numbers use the default country code (+91)." />

        {/* One field per {{variable}} — these are the real values that replace the
            placeholders at send time (name, date, city, …). */}
        {tplVars.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
              <Braces size={14} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>Variable values for this send</Typography>
            </Stack>
            <Stack spacing={1}>
              {tplVars.map((v) => (
                <Stack key={v.name} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Chip label={`{{${v.name}}}`} size="small" sx={{ fontWeight: 700, fontFamily: 'monospace', alignSelf: { xs: 'flex-start', sm: 'center' } }} />
                  <TextField size="small" fullWidth label="Value" value={vars[v.name] ?? ''}
                    onChange={(e) => setVar(v.name, e.target.value)} placeholder={v.example || `Value for {{${v.name}}}`} />
                </Stack>
              ))}
            </Stack>
            {template?.body && (
              <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>THIS RECIPIENT WILL SEE</Typography>
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {substituteVars(template.body, vars)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {template?.status !== 'APPROVED' && (
          <Alert severity="warning" sx={{ mt: 1.5, fontSize: 12.5 }}>
            This template is <b>{(TEMPLATE_STATUS[template?.status] || {}).label || template?.status}</b>. Meta only delivers
            approved templates to real numbers.
          </Alert>
        )}
        {template?.status === 'REJECTED' && template?.rejectedReason && (
          <RejectionNote reason={template.rejectedReason} sx={{ mt: 1.5 }} />
        )}
      </DialogContent>
      <DialogActions sx={waDialogActionsSx}>
        <Button onClick={onClose} color="inherit" disabled={sending}>Cancel</Button>
        <Button variant="contained" onClick={send} disabled={sending || !phone.trim()} startIcon={<Send size={15} />}>
          {sending ? 'Sending…' : 'Send test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Status filter chips above the grid.
const STATUS_FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'PENDING', label: 'In Review' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'PAUSED', label: 'Paused' },
];

const CATEGORY_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'MARKETING', label: 'Marketing' },
  { key: 'UTILITY', label: 'Utility' },
  { key: 'AUTHENTICATION', label: 'Auth' },
];

const WhatsAppTemplates = () => {
  const theme = useTheme();
  const mobile = useWaMobile();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [dupSeed, setDupSeed] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [testTarget, setTestTarget] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [toast, setToast] = useState('');

  const getCategoryCount = (key) => {
    if (key === 'ALL') return templates.length;
    return templates.filter((t) => {
      const cat = t.category?.toUpperCase() || '';
      if (key === 'AUTHENTICATION') {
        return cat === 'AUTHENTICATION' || cat === 'AUTH';
      }
      return cat === key;
    }).length;
  };

  // Load templates. On initial load this passively syncs from Meta; clicking
  // "Sync" runs the authoritative sync (pulls platform edits, prunes deletes)
  // and reports what changed.
  const load = (isManual = false) => {
    if (isManual) setSyncing(true); else setLoading(true);
    setError('');
    const request = isManual
      ? waApi.syncTemplates()
      : waApi.getTemplates().then((rows) => ({ templates: rows }));
    request
      .then((data) => {
        const rows = data?.templates;
        if (Array.isArray(rows)) setTemplates(rows);
        if (!isManual) return;
        if (data?.synced === false) {
          setToast(data?.msg || 'Could not sync with Meta.');
        } else if (data?.summary) {
          const { added = 0, updated = 0, removed = 0 } = data.summary;
          const parts = [];
          if (added) parts.push(`${added} added`);
          if (updated) parts.push(`${updated} updated`);
          if (removed) parts.push(`${removed} removed`);
          setToast(parts.length ? `Synced with Meta — ${parts.join(', ')}` : 'Synced with Meta — already up to date');
        } else {
          setToast('Synced with Meta');
        }
      })
      .catch((e) => {
        if (isManual) setToast(e?.response?.data?.error || e?.response?.data?.msg || 'Could not sync with Meta. Please try again.');
        else setError('Could not load templates. Check your connection and retry.');
      })
      .finally(() => { setLoading(false); setSyncing(false); });
  };
  useEffect(() => { load(); }, []);

  // Live status updates: Meta pushes approval/rejection through the webhook,
  // which the backend relays over Socket.IO as wa:template_status.
  useEffect(() => onWaEvent('wa:template_status', ({ name, status, reason }) => {
    setTemplates((prev) => prev.map((t) => (t.name === name
      ? { ...t, status, rejectedReason: status === 'APPROVED' ? '' : (reason || t.rejectedReason || ''), updatedAt: new Date().toISOString() }
      : t)));
    const label = (TEMPLATE_STATUS[status] || {}).label || status;
    const severity = status === 'APPROVED' ? 'success' : (status === 'REJECTED' ? 'error' : 'info');
    notify({
      title: `Template ${label}`,
      message: `“${name}” is now ${label}`,
      severity,
      system: true,
      tag: `wa-template-${name}`,
      url: '/whatsapp',
    });
  }), []);

  // A test send Meta accepts ("sent") can be DROPPED async (e.g. marketing pacing
  // #131049). Show that ONLY for the test you just sent — broadcasts also emit
  // wa:message_failed per dropped message and would otherwise spam this tab.
  const testMsgIds = useRef(new Set());
  useEffect(() => onWaEvent('wa:message_failed', ({ wa_message_id, recipient, code, reason }) => {
    if (!testMsgIds.current.has(wa_message_id)) return;
    testMsgIds.current.delete(wa_message_id);
    notify({
      title: 'Test message not delivered',
      message: `Test to ${recipient || 'recipient'} not delivered: ${reason}${code ? ` (#${code})` : ''}`,
      severity: 'error',
      system: true,
      url: '/whatsapp',
    });
  }), []);

  const seedFrom = (t, name) => ({
    name, category: t.category, language: t.language,
    headerType: t.headerType || 'NONE', header: t.header || '', headerMediaName: t.headerMediaName || '',
    headerMediaUrl: mediaSrc(t.headerMediaUrl),
    body: t.body || '', footer: t.footer || '', buttons: normalizeButtons(t.buttons || []),
    samples: varsToSamples(t.variables || []),
  });

  const openCreate = () => { setEditingId(null); setDupSeed(null); setOpen(true); };
  const openDuplicate = (t) => {
    const seed = seedFrom(t, `${t.name}_copy`);
    // A duplicate must get its own fresh upload (Meta needs a new sample handle),
    // so clear the carried image to surface the "No file selected" prompt.
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(seed.headerType)) { seed.headerMediaName = ''; seed.headerMediaUrl = ''; }
    setEditingId(null); setDupSeed(seed); setOpen(true);
  };
  const openEdit = (t) => { setEditingId(t.id); setDupSeed(seedFrom(t, t.name)); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditingId(null); };

  // Create or update, depending on whether the dialog was opened in edit mode.
  // Returns { ok, error } so the dialog can show a spinner, stay open on failure,
  // and surface Meta's actual reason inline.
  const handleSubmit = async (payload) => {
    try {
      if (editingId) {
        const tpl = await waApi.updateTemplate(editingId, toRequest(payload));
        setTemplates((p) => p.map((t) => (t.id === editingId ? tpl : t)));
        setToast('Template updated — back in review');
      } else {
        const tpl = await waApi.createTemplate(toRequest(payload));
        setTemplates((p) => [tpl, ...p]);
        setToast('Template submitted for approval');
      }
      return { ok: true };
    } catch (e) {
      const apiMsg = e?.response?.data?.error || e?.response?.data?.msg;
      return { ok: false, error: apiMsg || (editingId ? 'Could not update the template. Please try again.' : 'Could not submit the template. Please try again.') };
    }
  };

  const confirmRemove = async () => {
    const id = removeTarget?.id;
    if (!id) return;
    setRemoving(true);
    try {
      await waApi.deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      setToast('Template deleted');
      setRemoveTarget(null);
    } catch (e) {
      setToast(e?.response?.data?.msg || 'Could not delete the template. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const counts = templates.filter((t) => {
    if (selectedCategory === 'ALL') return true;
    const cat = t.category?.toUpperCase() || '';
    if (selectedCategory === 'AUTHENTICATION') {
      return cat === 'AUTHENTICATION' || cat === 'AUTH';
    }
    return cat === selectedCategory;
  }).reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  const visible = templates.filter((t) => {
    // 1. Status Filter
    const matchesStatus = filter === 'ALL' || t.status === filter;
    
    // 2. Category Filter
    let matchesCategory = true;
    if (selectedCategory !== 'ALL') {
      const cat = t.category?.toUpperCase() || '';
      if (selectedCategory === 'AUTHENTICATION') {
        matchesCategory = (cat === 'AUTHENTICATION' || cat === 'AUTH');
      } else {
        matchesCategory = (cat === selectedCategory);
      }
    }
    
    return matchesStatus && matchesCategory;
  });

  return (
    <Box>
      <WaSectionHeader
        title="Message Templates"
        subtitle="Required for any message sent outside the 24-hour window. Meta reviews each one before it can be used."
        action={(
          <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, '& > button': { flex: { xs: 1, sm: 'initial' } } }}>
            <Button variant="outlined" startIcon={<RefreshCw size={15} />} onClick={() => load(true)} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync'}
            </Button>
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate}>Create Template</Button>
          </Stack>
        )}
      />

      {!loading && !error && templates.length > 0 && (
        <Stack spacing={2} sx={{ mb: 2.5 }}>
          {/* Category Tabs UI */}
          <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 3, overflowX: 'auto', pb: 0.5 }}>
            {CATEGORY_TABS.map((tab) => {
              const isActive = selectedCategory === tab.key;
              const count = getCategoryCount(tab.key);
              return (
                <Box
                  key={tab.key}
                  onClick={() => {
                    setSelectedCategory(tab.key);
                    setFilter('ALL'); // Reset status filter on tab switch
                  }}
                  sx={{
                    position: 'relative',
                    pb: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 14,
                    fontFamily: 'Outfit, sans-serif',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      '& .tab-underline': { transform: 'scaleX(1)' }
                    },
                    transition: 'color 0.2s ease',
                  }}
                >
                  {tab.label}
                  <Chip
                    label={count}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.divider, 0.5),
                      color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                      transition: 'all 0.2s ease',
                    }}
                  />
                  <Box
                    className="tab-underline"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      bgcolor: theme.palette.primary.main,
                      transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.25s ease-out',
                      borderRadius: '3px 3px 0 0',
                    }}
                  />
                </Box>
              );
            })}
          </Box>

          {/* Status filter chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {STATUS_FILTERS.map((f) => {
              const count = f.key === 'ALL'
                ? templates.filter((t) => {
                    if (selectedCategory === 'ALL') return true;
                    const cat = t.category?.toUpperCase() || '';
                    if (selectedCategory === 'AUTHENTICATION') {
                      return cat === 'AUTHENTICATION' || cat === 'AUTH';
                    }
                    return cat === selectedCategory;
                  }).length
                : (counts[f.key] || 0);
              return (
                <Chip key={f.key} label={`${f.label} (${count})`} size="small"
                  color={filter === f.key ? 'primary' : 'default'}
                  variant={filter === f.key ? 'filled' : 'outlined'}
                  onClick={() => setFilter(f.key)} sx={{ fontWeight: 700 }} />
              );
            })}
          </Box>
        </Stack>
      )}

      {loading && (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={i} sx={waGridItem}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Skeleton variant="rounded" width={34} height={34} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="60%" height={18} />
                    <Skeleton width="40%" height={14} />
                  </Box>
                  <Skeleton variant="rounded" width={64} height={22} />
                </Stack>
                <Skeleton variant="rounded" height={88} sx={{ mb: 1 }} />
                <Skeleton width="50%" height={14} />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => load()}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && (
      <Grid container spacing={2}>
        {visible.map((t) => (
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={t.id} sx={waGridItem}>
            <Card sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0, mr: 1.5 }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={17} color={theme.palette.primary.main} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 800 }}>{t.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{t.category} · {t.language}</Typography>
                  </Box>
                </Stack>
                <StatusChip status={t.status} />
              </Stack>
              <Box sx={{ flex: 1, display: 'flex', mb: 1, minHeight: 150 }}>
                <TemplatePreview form={toPreviewForm(t)} compact />
              </Box>
              {t.status === 'REJECTED' && t.rejectedReason && (
                <RejectionNote reason={t.rejectedReason} sx={{ mb: 1 }} />
              )}
              {t.status === 'REJECTED' && !t.rejectedReason && (
                <Alert severity="error" sx={{ fontSize: 12, py: 0.5, mb: 1, '& .MuiAlert-message': { py: 0.5 } }}>
                  Rejected by Meta. No reason code was returned yet — click <b>Sync</b>, or open
                  WhatsApp Manager → Message Templates for the detailed note.
                </Alert>
              )}
              <Divider sx={{ mb: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled', minWidth: 0 }} noWrap>Updated {fmtDateTime(t.updatedAt)}</Typography>
                <Stack direction="row" sx={{ flexShrink: 0 }}>
                  <Tooltip title="Send test"><IconButton size="small" aria-label="Send test message" onClick={() => setTestTarget(t)}><Send size={15} /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" aria-label="Edit template" onClick={() => openEdit(t)}><Pencil size={15} /></IconButton></Tooltip>
                  <Tooltip title="Duplicate"><IconButton size="small" aria-label="Duplicate template" onClick={() => openDuplicate(t)}><Copy size={15} /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" aria-label="Delete template" onClick={() => setRemoveTarget(t)} sx={{ color: 'error.main' }}><Trash2 size={15} /></IconButton></Tooltip>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        ))}
        {visible.length === 0 && !error && (
          <Grid size={12}>
            <Card sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', boxShadow: 'none' }}>
              <MessageSquare size={28} color={theme.palette.text.disabled} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 1 }}>
                {templates.length === 0 ? 'No templates yet' : 'No templates match this filter'}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>
                {templates.length === 0
                  ? 'Create a template to message contacts outside the 24-hour window.'
                  : 'Try a different status filter or create a new template.'}
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
      )}

      <TemplateDialog open={open} onClose={closeDialog} onSubmit={handleSubmit} initial={dupSeed} isEdit={Boolean(editingId)} />

      <TestTemplateDialog template={testTarget} onClose={() => setTestTarget(null)} onResult={setToast}
        onSent={(id) => { if (id) testMsgIds.current.add(id); }} />

      {/* Delete confirmation */}
      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete this template?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            <b>{removeTarget?.name}</b> will be removed from this app and deleted from Meta. Any broadcast or automation
            that relies on it will stop working. This can’t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={waDialogActionsSx}>
          <Button onClick={() => setRemoveTarget(null)} color="inherit" disabled={removing}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmRemove} disabled={removing}
            startIcon={removing ? <CircularProgress size={15} color="inherit" /> : <Trash2 size={15} />}>
            {removing ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={/could not|error|fail|attach|require|invalid|reject|must|not set/i.test(toast) ? 'error' : 'success'} variant="filled" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppTemplates;
